// @flow
import React, { Component } from 'react';
import moment from 'moment';
import _ from 'lodash';
import { Header, Grid, Container, Icon, Popup, Segment } from 'semantic-grab-ui/src';
import { GroupCommsMetrics, CommsMetrics } from 'types/trident-campaigns';
import { channelStatsMapping, commsChannels } from 'data/trident-campaigns';
import MetricMultiSelectDropdown from './MetricMultiSelectDropdown';
import MetricsChart from './MetricsChart';
import AnimatedNumber from './AnimatedNumber';

type Props = {
  metrics: GroupCommsMetrics,
};

const MaxChannelsViewable = 3;

const ChannelColorMapping = {
  [commsChannels.push]: '#AFE6FA',
  [commsChannels.pushinbox]: '#FAD94B',
  [commsChannels.inbox]: '#3D7CC4',
  [commsChannels.email]: '#3AB7BC',
  [commsChannels.pciPush]: '#FAD94B',
  [commsChannels.pciInbox]: '#FAB16E',
};

// MetricsCell is the component for showing metrics for one action node
// it displays comms channel selection, open rate, click-through rate and metrics bar chart
export default class MetricsCell extends Component {
  props: Props;

  constructor(props: Props) {
    super(props);
    const channels = _.keys(props.metrics.metrics);
    this.state = {
      selectedChannels: channels.length < MaxChannelsViewable ? channels : _.slice(channels, 0, MaxChannelsViewable),
    };
  }

  getMetricsUnavailableMessage = () => {
    const meta = this.props.metrics.meta;
    if (!meta.hasTrackableComms) {
      return 'No trackable comms action (Push/ Inbox/ Push-chained-inbox/ Email) is used.';
    }
    if (!meta.isBulkComms && !meta.hasTrigger) {
      return 'The comms action has never been triggered yet.';
    }
    if (meta.startTime && moment().diff(meta.startTime, 'days') < 3) {
      return `${meta.isBulkComms ? 'Schedule' : 'Scenario'} started less than 3 days ago. Metrics will be available soon.`;
    }
    return 'Metrics unavailable due to unknown reasons, please contact Trident support';
  };

  getChannelMetricsStats = (channel: string, stats: CommsMetrics) => {
    const statsKeys = _.get(channelStatsMapping, channel, []);
    return statsKeys.map(key => {
      return {
        key,
        value: _.get(stats, key, 0),
      };
    });
  };

  handleChannelSelection = (channels: Array<string>) => {
    this.setState({
      selectedChannels: channels,
    });
  };

  formatPercentage = (num: number) => {
    return parseFloat(num * 100).toFixed(2);
  };

  renderRatesDisplay = (rates: Array<Object>) => {
    return (
      <Grid>
        <Grid.Row style={{ paddingTop: 16, paddingBottom: 16 }}>
          {
            _.map(rates, o => {
              return (<Grid.Column style={{ paddingRight: '70px' }} key={o.channel}>
                <p style={{ fontSize: '18px', marginBottom: '2px' }}>
                  <span style={{ fontWeight: 100, color: _.get(ChannelColorMapping, o.channel, 'black'), paddingRight: 4 }}>•</span>
                  <AnimatedNumber number={this.formatPercentage(o.rate)} duration={700} suffix="%" />
                </p>
                { // display the uplift
                  !_.isUndefined(o.uplift) && Math.abs(o.uplift) >= 0.0001 &&
                  <Popup
                    trigger={
                      <p style={{ fontSize: '12px', paddingLeft: '12px', color: o.uplift > 0 ? '#00B14F' : '#F55538' }}>
                        {`${o.uplift > 0 ? '▲' : '▼'}${this.formatPercentage(Math.abs(o.uplift))}%`}
                      </p>
                    }
                    position="bottom left"
                  >
                    <React.Fragment>
                      <p>{`${o.uplift > 0 ? 'Positive' : 'Negative'} uplift is measured against the control group.`}</p>
                      <p />
                      <p>This only appears if you have a control group for the selected channel.</p>
                    </React.Fragment>
                  </Popup>
                }
              </Grid.Column>);
            })
          }
        </Grid.Row>
      </Grid>
    );
  };

  render() {
    const { metrics } = this.props;
    const { selectedChannels } = this.state;
    const openRates = []; // list of {channel, rate, uplift}
    const clickThroughRates = []; // list of {channel, rate, uplift}

    const records = [];
    _.map(metrics.metrics, (stats, channel) => {
      if (_.includes(selectedChannels, channel)) {
        records.push({
          channel,
          counts: this.getChannelMetricsStats(channel, stats),
        });

        const analytics = _.get(metrics.analytics, channel, {});
        if ('openRate' in analytics) {
          openRates.push({ channel, rate: analytics.openRate, uplift: _.get(analytics, 'uplift.openRate') });
        }
        if ('clickThroughRate' in analytics) {
          clickThroughRates.push({ channel, rate: analytics.clickThroughRate, uplift: _.get(analytics, 'uplift.clickThroughRate') });
        }
      }
    });

    return (

      <div style={{ paddingRight: '50px', paddingLeft: '10px', height: '100%' }}>
        {
          _.isEmpty(metrics.metrics) &&
            <React.Fragment>
              <Header as="h3" style={{ marginTop: 8, fontSize: 18 }}>
                {_.truncate(metrics.name, { length: 30 })}
              </Header>
              <Segment placeholder style={{ height: '100%', borderWidth: 0, boxShadow: 'none', backgroundColor: '#FFFFFF' }}>
                <Header icon>
                  <Icon name="chart bar" style={{ color: '#E5E5E5', fontSize: 35 }} />
                  <p style={{ fontSize: 14, fontWeight: 'normal', color: '#707070' }}>No metrics available &nbsp;&nbsp;<Popup content={this.getMetricsUnavailableMessage()} trigger={<Icon style={{ fontSize: '1em', display: 'inline' }} name="info circle" />} /></p>
                </Header>
              </Segment>
            </React.Fragment>
        }
        {
          !_.isEmpty(metrics.metrics) &&
            <React.Fragment>
              <Grid>
                <Grid.Row>
                  <Grid.Column width={10} style={{ paddingLeft: 4 }}>
                    <Header as="h3" style={{ marginTop: 8, fontSize: 18 }}>
                      {_.truncate(metrics.name, { length: 30 })}
                    </Header>
                  </Grid.Column>
                  <Grid.Column floated="right" width={6} style={{ paddingRight: 0 }}>
                    <MetricMultiSelectDropdown
                      options={_.keys(metrics.metrics)}
                      selected={selectedChannels}
                      onSelect={this.handleChannelSelection}
                      maxSelectable={MaxChannelsViewable}
                      style={{ float: 'right' }}
                      entity="channel"
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row divided style={{ backgroundColor: '#F7F9FB', marginTop: '10px', marginBottom: '24px', paddingTop: '16px' }}>
                  {
                    openRates.length > 0 &&
                    <Grid.Column width={8}>
                      <Container>
                        <p style={{ fontSize: '14px' }}>Open Rate <Popup content="Percentage of how many users opened your campaign via the respective channel." trigger={<Icon name="info circle" style={{ color: '#707070' }} />} /></p>
                        {this.renderRatesDisplay(openRates)}
                      </Container>
                    </Grid.Column>
                  }
                  {
                    clickThroughRates.length > 0 &&
                    <Grid.Column width={8}>
                      <Container>
                        <p style={{ fontSize: '14px' }}>Click Through Rate <Popup content="Percentage of how many users (among those who already opened the campaign) clicked on your link via the respective channel." trigger={<Icon name="info circle" style={{ color: '#707070' }} />} /></p>
                        {this.renderRatesDisplay(clickThroughRates)}
                      </Container>
                    </Grid.Column>
                  }
                </Grid.Row>
              </Grid>
              <MetricsChart records={records} channelColorMapping={ChannelColorMapping} />
            </React.Fragment>
        }
      </div>
    );
  }
}
